import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, CornerDownLeft, SquareTerminal } from 'lucide-react'
import type { ChatClientKey } from '@one-deployment/protocol'
import { Button } from '@/components/shadcn/button'

type Key = ChatClientKey['key']

/** The numbered choices a terminal menu lists (`❯ 1. Yes`), for one-click answers. */
export function screenChoices(text: string): { key: Key; label: string }[] {
  const out: { key: Key; label: string }[] = []
  for (const line of text.split('\n')) {
    const m = /^[\s│|]*(?:[❯>›]\s*)?([1-9])[.)]\s+(.+?)[\s│|]*$/.exec(line)
    if (m && !out.some((o) => o.key === m[1])) out.push({ key: m[1] as Key, label: m[2]! })
  }
  return out
}

/**
 * Claude's terminal waits on a key for something the transcript never shows:
 * a dialog, a menu, a permission ask. Shown here with the keys to answer it,
 * so a message is not left hanging behind it.
 */
export function ScreenCard({ text, readOnly, onKey }: { text: string; readOnly: boolean; onKey: (k: Key) => void }) {
  const choices = screenChoices(text)
  const key = (k: Key, label: React.ReactNode, title: string) => (
    <Button key={k} variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" title={title} disabled={readOnly} onClick={() => onKey(k)}>{label}</Button>
  )
  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-2">
      <div className="flex flex-col gap-2 rounded-lg border border-warn/40 bg-warn/5 p-3">
        <div className="flex items-center gap-2 text-xs font-medium text-warn"><SquareTerminal size={14} />Claude is waiting for you in its terminal</div>
        <pre className="max-h-56 overflow-auto whitespace-pre rounded-lg border bg-bg p-2.5 font-mono text-[11px] leading-snug text-fg-muted">{text}</pre>
        {!!choices.length && (
          <div className="flex flex-wrap gap-1.5">
            {choices.map((c) => key(c.key, <><span className="font-mono text-fg-faint">{c.key}</span>{c.label}</>, `Press ${c.key}`))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          {key('Up', <ArrowUp size={12} />, 'Up')}
          {key('Down', <ArrowDown size={12} />, 'Down')}
          {key('Left', <ArrowLeft size={12} />, 'Left')}
          {key('Right', <ArrowRight size={12} />, 'Right')}
          {key('Space', 'Space', 'Space (tick or untick)')}
          {key('Enter', <><CornerDownLeft size={12} />Enter</>, 'Enter (confirm)')}
          {key('Escape', 'Esc', 'Esc (cancel or skip)')}
          {readOnly && <span className="text-[11px] text-fg-faint">Read-only: you can watch, not answer.</span>}
        </div>
      </div>
    </div>
  )
}
